import api from '@api/apiServices';
import LtrProvider from '@Components/Common/LtrProvider';
import useDialog from '@hooks/useDialog';
import {
    AddRounded,
    CheckRounded,
    CloseRounded,
    EditRounded,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Popover,
    Slider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hueToLabelColor } from '@utils/contactUtils';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// TODO: Improve Edit label UI and language

const utf8ToBase64 = str => {
    return btoa(
        encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (match, p1) =>
            String.fromCharCode(parseInt(p1, 16)),
        ),
    );
};

const EditLabel = ({
    anchorEL,
    open,
    onClose,
    addLabel,
    label,
    onLabelEdit,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const deleteDialog = useDialog();

    const invalidateLabels = () =>
        queryClient.invalidateQueries({
            queryKey: ['contactLabels'],
        });

    const { register, control, getValues } = useForm({
        defaultValues: {
            name: label?.name || null,
            hue: label?.hue || Math.round(Math.random() * 360),
        },
    });

    const { mutate: createLabel } = useMutation({
        mutationFn: () => api.contacts.labels.post(getValues()),
        onSuccess: () => {
            invalidateLabels();
            addLabel(getValues());
            onLabelEdit?.();
            onClose();
        },
    });

    const { mutate: updateLabel } = useMutation({
        mutationFn: () =>
            api.contacts.labels.patch(utf8ToBase64(label.name), getValues()),
        onSuccess: () => {
            invalidateLabels();
            onLabelEdit?.();
            onClose();
        },
    });

    const deleteLabelMutation = useMutation({
        mutationFn: data => api.contacts.labels.delete(data),
        onSuccess: () => {
            invalidateLabels();
            onLabelEdit?.();
            deleteDialog.close();
            onClose();
        },
    });

    const openDeleteLabelDialog = () => {
        deleteDialog.open({
            title: t('prompts:title.deleteLabel'),
            content: t('prompts:content.deleteLabel'),
            confirmLabel: t('prompts:confirm.delete'),
            confirmMutation: deleteLabelMutation,
            onConfirm: () => utf8ToBase64(label.name),
            confirmButtonProps: {
                color: 'error',
            },
        });
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEL}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        p: 2,
                    },
                },
            }}
        >
            <Typography variant="titleLarge" mb={2}>
                {label ? t('Edit label') : t('Create label')}
            </Typography>
            <Stack>
                <TextField
                    size="small"
                    fullWidth
                    {...register('name')}
                    placeholder={t('Enter label')}
                />
                <LtrProvider>
                    <Controller
                        name="hue"
                        control={control}
                        render={({ field: { onChange, ...fieldProps } }) => (
                            <Slider
                                {...fieldProps}
                                onChange={(_, value) => onChange(value)}
                                max={360}
                                track={false}
                                sx={{
                                    color: hueToLabelColor(fieldProps.value),
                                    '& .MuiSlider-rail': {
                                        height: 12,
                                        opacity: 'unset',
                                        background: `-webkit-gradient(linear, left top, right top, from(${hueToLabelColor(
                                            0,
                                        )}), color-stop(17%, ${hueToLabelColor(
                                            60,
                                        )}), color-stop(33%, ${hueToLabelColor(
                                            120,
                                        )}), color-stop(50%, ${hueToLabelColor(
                                            180,
                                        )}), color-stop(66%, ${hueToLabelColor(
                                            240,
                                        )}), color-stop(83%, ${hueToLabelColor(
                                            300,
                                        )}), to(${hueToLabelColor(360)}))`,
                                    },
                                }}
                            />
                        )}
                    />
                </LtrProvider>
                <Stack direction="row" justifyContent="flex-end">
                    {label && (
                        <Button
                            onClick={openDeleteLabelDialog}
                            variant="text"
                            color="error"
                        >
                            {t('Delete')}
                        </Button>
                    )}
                    <Button onClick={label ? updateLabel : createLabel}>
                        {t('Save')}
                    </Button>
                </Stack>
            </Stack>
        </Popover>
    );
};

const Label = ({
    anchorEL,
    selected,
    label,
    addLabel,
    removeLabel,
    onLabelEdit,
}) => {
    const [editLabelOpen, setEditLabelOpen] = useState(false);

    return (
        <Stack direction="row" alignItems="center" width={240}>
            <Box
                sx={{
                    backgroundColor: hueToLabelColor(label.hue),
                    borderRadius: them => them.shape.xs,
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant="bodyLarge"
                    color="textSecondary"
                    sx={{ py: 0.5, px: 1, flex: 1 }}
                >
                    {label.name}
                </Typography>
                <IconButton
                    color="primary"
                    onClick={() => setEditLabelOpen(true)}
                >
                    <EditRounded />
                </IconButton>
            </Box>
            <IconButton
                color="primary"
                onClick={
                    selected ? () => removeLabel(label) : () => addLabel(label)
                }
            >
                {selected ? <CheckRounded /> : <AddRounded />}
            </IconButton>
            <EditLabel
                label={label}
                open={editLabelOpen}
                addLabel={addLabel}
                anchorEL={anchorEL}
                onClose={() => setEditLabelOpen(false)}
                onLabelEdit={onLabelEdit}
            />
        </Stack>
    );
};

const SelectLabel = ({
    anchorEL,
    onClose,
    selectedLabels,
    addLabel,
    removeLabel,
    onLabelEdit,
}) => {
    const { t } = useTranslation();
    const [createLabelOpen, setCreateLabelOpen] = useState(false);
    const [search, setSearch] = useState('');

    const { data: labels, isLoading } = useQuery({
        queryKey: ['contactLabels'],
        queryFn: () => api.contacts.labels.get(),
    });

    const filteredLabels =
        labels?.filter(label =>
            label.name.toLowerCase().includes(search.toLowerCase()),
        ) || [];

    return (
        <Popover
            open={Boolean(anchorEL)}
            anchorEl={anchorEL}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        p: 2,
                    },
                },
            }}
        >
            <Stack spacing={2} width={240}>
                <Typography variant="labelLarge">
                    {t('Select label')}
                </Typography>
                <TextField
                    variant="outlined"
                    size="small"
                    type="search"
                    fullWidth
                    placeholder={t('Search labels') + '...'}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <Stack spacing={1} height={300} overflow="auto">
                    {filteredLabels.map(label => (
                        <Label
                            key={label.name}
                            label={label}
                            selected={selectedLabels.some(
                                l => l.name === label.name,
                            )}
                            addLabel={addLabel}
                            removeLabel={removeLabel}
                            anchorEL={anchorEL}
                            onLabelEdit={onLabelEdit}
                        />
                    ))}
                </Stack>
                <Button
                    variant="tonal"
                    onClick={() => setCreateLabelOpen(true)}
                >
                    {t('Create New Label')}
                </Button>
            </Stack>
            <EditLabel
                open={createLabelOpen}
                anchorEL={anchorEL}
                onClose={() => setCreateLabelOpen(false)}
                addLabel={addLabel}
                onLabelEdit={onLabelEdit}
            />
        </Popover>
    );
};

const Labels = ({ selectedLabels, addLabel, removeLabel, onLabelEdit }) => {
    const { t } = useTranslation();
    const [anchorEL, setAnchorEl] = useState(null);

    return (
        <Box display="flex" flexWrap="wrap" gap={1}>
            {selectedLabels.map(label => (
                <Chip
                    sx={{
                        backgroundColor: hueToLabelColor(label.hue),
                    }}
                    key={label.name}
                    label={label.name}
                    deleteIcon={<CloseRounded />}
                    onDelete={() => removeLabel(label)}
                />
            ))}
            <Chip
                label={t('Label')}
                variant="outlined"
                color="primary"
                onClick={e => setAnchorEl(e.currentTarget)}
                icon={<AddRounded />}
            />
            <SelectLabel
                anchorEL={anchorEL}
                onClose={() => setAnchorEl(null)}
                selectedLabels={selectedLabels}
                addLabel={addLabel}
                removeLabel={removeLabel}
                onLabelEdit={onLabelEdit}
            />
        </Box>
    );
};

export default Labels;
